/**
 * Forum Topic Card Component
 * 
 * Modern card display for forum topics
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAvatar } from '@/hooks/useAvatar'
import {
  MessageCircle,
  Eye,
  Heart,
  Pin,
  Lock,
  Clock,
  TrendingUp,
  ChevronRight,
} from 'lucide-react'
import type { ForumTopic } from '@/lib/services/forumStore.service'
import { useTranslation } from 'react-i18next';

interface ForumTopicCardProps {
  topic: ForumTopic
  categorySlug: string
  boardSlug: string
}

export default function ForumTopicCard({
  topic,
  categorySlug,
  boardSlug,
}: ForumTopicCardProps) {
  const { t } = useTranslation();
  const { getAvatarUrl } = useAvatar()

  function formatDate(dateString: string) {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (hours < 1) return t('store.forum.timeNow')
    if (hours < 24) return t('store.forum.timeHours', { count: hours })
    if (days === 1) return t('store.forum.timeYesterday')
    if (days < 7) return t('store.forum.timeDays', { count: days })
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  const isHot = topic.postsCount > 20
  const isNew = new Date().getTime() - new Date(topic.createdAt).getTime() < 86400000 // 24h

  return (
    <Link to={`/forum/${categorySlug}/${boardSlug}/${topic.slug}`} className="block mb-6">
      <Card className="border-card-border hover:border-primary/50 transition-all duration-300 hover:shadow-glow group">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            {/* Author Avatar */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg border-2 border-card-border bg-gradient-to-br from-primary via-accent to-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
                <img
                  src={getAvatarUrl((topic.authorHytaleNick && topic.authorHytaleNick.trim() !== '') ? topic.authorHytaleNick : topic.authorName, 64)}
                  alt={topic.authorName || t('store.forum.player')}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                    if (target.parentElement) {
                      target.parentElement.textContent = topic.authorName ? topic.authorName[0].toUpperCase() : '?'
                    }
                  }}
                />
              </div>
            </div>

            {/* Topic Content */}
            <div className="flex-1 min-w-0">
              {/* Title Row */}
              <div className="flex items-start gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {topic.isPinned && (
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/30"
                    >
                      <Pin className="w-3 h-3 mr-1" />
                      {t('store.forum.pinned')}
                    </Badge>
                  )}
                  {topic.isLocked && (
                    <Badge
                      variant="outline"
                      className="bg-warning/10 text-warning border-warning/30"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      {t('store.forum.locked')}
                    </Badge>
                  )}
                  {isHot && !topic.isPinned && (
                    <Badge
                      variant="secondary"
                      className="bg-destructive/10 text-destructive border-destructive/30"
                    >
                      🔥 Popular
                    </Badge>
                  )}
                  {isNew && !topic.isPinned && (
                    <Badge
                      variant="secondary"
                      className="bg-success/10 text-success border-success/30"
                    >
                      ✨ {t('store.forum.new')}
                    </Badge>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all ml-auto flex-shrink-0" />
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                {topic.title}
              </h3>

              {/* Author and Date Info */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <span>
                  Iniciado por{' '}
                  <span className="font-medium text-foreground">
                    {topic.authorName || t('store.forum.unknown')}
                  </span>
                </span>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatDate(topic.createdAt)}</span>
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 rounded-lg">
                  <MessageCircle className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">
                    {topic.postsCount}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t(topic.postsCount === 1 ? 'store.forum.replyCount' : 'store.forum.replyCountPlural')}
                  </span>
                </div>

                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 rounded-lg">
                  <Eye className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">
                    {topic.views}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {topic.views === 1 ? 'visualização' : 'visualizações'}
                  </span>
                </div>

                {topic.reactionsCount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-destructive/5 rounded-lg">
                    <Heart className="w-4 h-4 text-destructive" />
                    <span className="text-sm font-semibold text-foreground">
                      {topic.reactionsCount}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {topic.reactionsCount === 1 ? 'reação' : 'reações'}
                    </span>
                  </div>
                )}

                {/* Last Activity */}
                <div className="hidden sm:flex items-center gap-2 ml-auto text-xs text-muted-foreground">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span>Última atividade {formatDate(topic.lastPostAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}
